import { useMemo, useState, type ReactNode } from 'react';
import { Button } from './Button';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  width?: string;
  /** Value used for sorting (and column-scoped search). Defaults to row[key]. */
  value?: (row: T) => string | number | boolean | null | undefined;
  /** Default: true when header is non-empty. */
  sortable?: boolean;
  /** Default: true when header is non-empty. */
  searchable?: boolean;
  /** Horizontal alignment of header and cells — use 'right' for money/number columns. */
  align?: 'left' | 'right';
}

interface DataTableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T & string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  /** When set and the dataset is empty, show a load failure instead of emptyMessage. */
  loadError?: string | null;
  /**
   * When set, a Retry button is shown next to loadError. With onRetry, a failed refresh
   * that still has (older) rows also shows the error above the table.
   */
  onRetry?: () => void;
  loading?: boolean;
  dense?: boolean;
  /**
   * Search box + clickable sort headers.
   * Default true — set false for fixed-order lists (e.g. Zones).
   */
  filterSort?: boolean;
  searchPlaceholder?: string;
}

type SortDir = 'asc' | 'desc';

function cellValue<T extends object>(row: T, col: Column<T>): string | number | boolean | null {
  if (col.value) {
    const v = col.value(row);
    return v === undefined ? null : v;
  }
  const raw = (row as Record<string, unknown>)[col.key];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') return raw;
  return null;
}

function compareValues(a: string | number | boolean | null, b: string | number | boolean | null, dir: SortDir): number {
  const mul = dir === 'asc' ? 1 : -1;
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mul;
  if (typeof a === 'boolean' && typeof b === 'boolean') return (Number(a) - Number(b)) * mul;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }) * mul;
}

function rowMatchesQuery<T extends object>(row: T, columns: Column<T>[], query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const parts: string[] = [];
  for (const col of columns) {
    if (col.searchable === false) continue;
    if (!col.header && col.searchable !== true) continue;
    const v = cellValue(row, col);
    if (v !== null) parts.push(String(v));
  }

  // Also index top-level primitives so custom render columns still match
  for (const value of Object.values(row as Record<string, unknown>)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(String(value));
    }
  }

  return parts.join(' ').toLowerCase().includes(q);
}

function isColumnSortable<T>(col: Column<T>): boolean {
  if (col.sortable === false) return false;
  if (col.sortable === true) return true;
  return Boolean(col.header?.trim());
}

export function DataTable<T extends object>({
  columns,
  data,
  keyField,
  onRowClick,
  emptyMessage = 'No records found',
  loadError = null,
  onRetry,
  loading = false,
  dense = false,
  filterSort = true,
  searchPlaceholder = 'Filter…',
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filteredSorted = useMemo(() => {
    let rows = data;
    if (filterSort && query.trim()) {
      rows = rows.filter((row) => rowMatchesQuery(row, columns, query));
    }
    if (filterSort && sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col && isColumnSortable(col)) {
        rows = [...rows].sort((a, b) =>
          compareValues(cellValue(a, col), cellValue(b, col), sortDir),
        );
      }
    }
    return rows;
  }, [columns, data, filterSort, query, sortDir, sortKey]);

  const toggleSort = (col: Column<T>) => {
    if (!filterSort || !isColumnSortable(col)) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  };

  // Only blank the table on the first load; on refresh keep rows visible so
  // the user keeps their place (a small "Updating…" note shows instead).
  if (loading && data.length === 0) {
    return (
      <div className="table-loading">
        <div className="spinner" />
      </div>
    );
  }

  const showToolbar = filterSort && data.length > 0;
  const emptyAfterFilter = filterSort && data.length > 0 && filteredSorted.length === 0;

  return (
    <div className="data-table-shell">
      {showToolbar && (
        <div className="table-toolbar">
          <input
            type="search"
            className="table-filter"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label="Filter table"
          />
          <span className="table-count">
            {loading && <span className="table-updating">Updating… </span>}
            {query.trim()
              ? `${filteredSorted.length} of ${data.length}`
              : `${data.length} record${data.length === 1 ? '' : 's'}`}
          </span>
        </div>
      )}

      {loadError && onRetry && data.length > 0 && (
        <div className="table-stale-error" role="alert">
          <span>{loadError}</span>
          <Button type="button" size="sm" variant="secondary" onClick={() => onRetry()}>
            Retry
          </Button>
        </div>
      )}

      {data.length === 0 ? (
        <div className={`table-empty${loadError ? ' table-load-error' : ''}`}>
          {loadError ?? emptyMessage}
          {loadError && onRetry && (
            <>
              {' '}
              <Button type="button" size="sm" variant="secondary" onClick={() => onRetry()}>
                Retry
              </Button>
            </>
          )}
        </div>
      ) : emptyAfterFilter ? (
        <div className="table-empty">No rows match “{query.trim()}”</div>
      ) : (
        <div className="data-table-wrap">
          <table className={`data-table ${dense ? 'dense' : ''}`}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const sortable = filterSort && isColumnSortable(col);
                  const active = sortKey === col.key;
                  return (
                    <th
                      key={col.key}
                      style={{ width: col.width, textAlign: col.align }}
                      className={sortable ? 'sortable' : undefined}
                      aria-sort={
                        sortable && active
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                      onClick={() => toggleSort(col)}
                    >
                      {sortable ? (
                        <button type="button" className="th-inner th-sort-btn">
                          {col.header}
                          <span className={`sort-indicator ${active ? 'active' : ''}`} aria-hidden>
                            {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </button>
                      ) : (
                        <span className="th-inner">{col.header}</span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((row) => (
                <tr
                  key={String((row as Record<string, unknown>)[keyField])}
                  onClick={() => onRowClick?.(row)}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  className={onRowClick ? 'clickable' : ''}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={col.align ? { textAlign: col.align } : undefined}
                      className={col.align === 'right' ? 'cell-num' : undefined}
                    >
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .data-table-shell {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }
        .table-toolbar {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          flex-wrap: wrap;
        }
        .table-filter {
          flex: 1;
          min-width: 180px;
          max-width: 360px;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-surface);
          font-size: 0.875rem;
          color: var(--color-ink);
        }
        .table-filter:focus {
          outline: 2px solid var(--color-signal);
          outline-offset: 1px;
        }
        .table-count {
          font-size: 0.8125rem;
          color: var(--color-text-muted);
          white-space: nowrap;
        }
        .data-table-wrap {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          overflow: auto;
        }
        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9375rem;
        }
        .data-table.dense {
          font-size: 0.8125rem;
        }
        .data-table th {
          text-align: left;
          padding: var(--space-md);
          background: var(--color-sand);
          font-weight: 600;
          font-size: 0.8125rem;
          color: var(--color-charcoal-light);
          border-bottom: 1px solid var(--color-border);
          white-space: nowrap;
          user-select: none;
        }
        .data-table th.sortable {
          cursor: pointer;
        }
        .data-table th.sortable:hover {
          color: var(--color-ink);
        }
        .th-sort-btn {
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
          cursor: pointer;
        }
        .table-updating { color: var(--color-text-muted); font-style: italic; }
        .data-table tbody tr.clickable:focus-visible {
          outline: 2px solid var(--color-signal);
          outline-offset: -2px;
        }
        .th-inner {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }
        .sort-indicator {
          font-size: 0.7rem;
          opacity: 0.35;
          font-weight: 400;
        }
        .sort-indicator.active {
          opacity: 1;
          color: var(--color-signal);
        }
        .data-table.dense th {
          padding: var(--space-sm) var(--space-md);
        }
        .data-table td {
          padding: var(--space-md);
          border-bottom: 1px solid var(--color-border);
        }
        .data-table.dense td {
          padding: var(--space-sm) var(--space-md);
        }
        .data-table td.cell-num {
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .data-table tbody tr:last-child td {
          border-bottom: none;
        }
        .data-table tbody tr.clickable {
          cursor: pointer;
        }
        .data-table tbody tr.clickable:hover {
          background: var(--color-sand-light);
        }
        .table-loading, .table-empty {
          padding: var(--space-2xl);
          text-align: center;
          color: var(--color-text-muted);
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
        }
        .table-load-error {
          color: var(--color-urgent, #c0392b);
        }
        .table-stale-error {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-md);
          flex-wrap: wrap;
          color: var(--color-urgent, #c0392b);
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}
